// API Service with JWT Authentication Support
// API URL is configured via environment variable in .env file
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  getToken() {
    return localStorage.getItem("authToken");
  }

  setToken(token) {
    localStorage.setItem("authToken", token);
  }

  removeToken() {
    localStorage.removeItem("authToken");
  }

  getHeaders(includeAuth = true) {
    const headers = {
      "Content-Type": "application/json",
    };
    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    return headers;
  }

  async request(endpoint, options = {}) {
    const { includeAuth = true, ...fetchOptions } = options;

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...fetchOptions,
      headers: {
        ...this.getHeaders(includeAuth),
        ...fetchOptions.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || "Request failed");
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  // Auth endpoints
  async login(username, password) {
    const data = await this.request("/users/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
      includeAuth: false,
    });
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async register(userData) {
    return this.request("/users/register", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  async logout() {
    // Best-effort — tell the server so it logs a USER_LOGOUT audit event.
    // We swallow errors (offline, network) because the client-side cleanup
    // below must always happen regardless of server response.
    try {
      await this.request("/users/logout", { method: "POST" });
    } catch {
      /* ignored — offline logout still clears local state */
    }
    this.removeToken();
    localStorage.removeItem("user");
  }

  // Users endpoints
  async getCurrentUser() {
    return this.request("/users/me");
  }

  async getUsers(page = 1, limit = 10) {
    return this.request(`/users?page=${page}&limit=${limit}`);
  }

  async getUserById(userId) {
    return this.request(`/users/${userId}`);
  }

  async updateUser(userId, userData) {
    return this.request(`/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(userId) {
    return this.request(`/users/${userId}`, {
      method: "DELETE",
    });
  }

  // Products endpoints (admin only)
  async getProducts() {
    return this.request("/products");
  }

  async getProductInventory() {
    return this.request("/products/inventory");
  }

  // Audit log
  async getAuditEvents(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") return;
      qs.set(k, Array.isArray(v) ? v.join(",") : String(v));
    });
    const q = qs.toString();
    return this.request(`/audit-events${q ? `?${q}` : ""}`);
  }

  async getAuditDistincts() {
    return this.request("/audit-events/distinct");
  }

  async seedAuditEvents(body = {}) {
    return this.request("/audit-events/seed", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async clearAuditEvents(password) {
    // Server requires a shared secret (AUDIT_CLEAR_PASSWORD env var) in
    // addition to admin auth — being signed in isn't enough for this
    // irreversible action. Express parses DELETE bodies fine.
    return this.request("/audit-events", {
      method: "DELETE",
      body: JSON.stringify({ password: password || "" }),
    });
  }

  async clearExpiryLog(password) {
    // Deletes only expiry-family events (TAG_ENDDATE_*, TAG_NEARING_END,
    // TAG_EXPIRED). Gated by EXPIRY_CLEAR_PASSWORD env var on the server.
    return this.request("/audit-events/expiry", {
      method: "DELETE",
      body: JSON.stringify({ password: password || "" }),
    });
  }

  async createProduct(productData) {
    return this.request("/products", {
      method: "POST",
      body: JSON.stringify(productData),
    });
  }

  async updateProduct(productId, productData) {
    return this.request(`/products/${productId}`, {
      method: "PUT",
      body: JSON.stringify(productData),
    });
  }

  async deleteProduct(productId) {
    return this.request(`/products/${productId}`, {
      method: "DELETE",
    });
  }

  async uploadProductImage(file) {
    const formData = new FormData();
    formData.append("image", file);

    const response = await fetch(`${this.baseUrl}/products/upload-image`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.getToken()}`,
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.message || "Image upload failed");
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  }

  // Locations endpoints
  async getLocations(page = 1, limit = 100) {
    return this.request(`/locations?page=${page}&limit=${limit}`);
  }

  async getLocationById(locationId) {
    return this.request(`/locations/${locationId}`);
  }

  async createLocation(locationData) {
    return this.request("/locations", {
      method: "POST",
      body: JSON.stringify(locationData),
    });
  }

  async updateLocation(locationId, locationData) {
    return this.request(`/locations/${locationId}`, {
      method: "PUT",
      body: JSON.stringify(locationData),
    });
  }

  async deleteLocation(locationId, options = {}) {
    const params = new URLSearchParams();
    if (options.tagAction) params.append("tagAction", options.tagAction);
    if (options.moveTagsToRid != null) params.append("moveTagsToRid", options.moveTagsToRid);

    const query = params.toString() ? `?${params.toString()}` : "";
    return this.request(`/locations/${locationId}${query}`, {
      method: "DELETE",
    });
  }

  async getLocationWarehouses(locationId, page = 1, limit = 100) {
    return this.request(`/locations/${locationId}/warehouses?page=${page}&limit=${limit}`);
  }

  async assignWarehouseToLocation(locationId, warehouseId) {
    return this.request(`/locations/${locationId}/warehouses`, {
      method: "POST",
      body: JSON.stringify({ warehouseId }),
    });
  }

  async removeWarehouseFromLocation(locationId, warehouseId) {
    return this.request(`/locations/${locationId}/warehouses/${warehouseId}`, {
      method: "DELETE",
    });
  }

  // Warehouses endpoints
  async getWarehouses(page = 1, limit = 100) {
    return this.request(`/warehouses?page=${page}&limit=${limit}`);
  }

  async getWarehouseById(warehouseId) {
    return this.request(`/warehouses/${warehouseId}`);
  }

  async createWarehouse(warehouseData) {
    return this.request("/warehouses", {
      method: "POST",
      body: JSON.stringify(warehouseData),
    });
  }

  async updateWarehouse(warehouseId, warehouseData) {
    return this.request(`/warehouses/${warehouseId}`, {
      method: "PUT",
      body: JSON.stringify(warehouseData),
    });
  }

  async deleteWarehouse(warehouseId, options = {}) {
    const params = new URLSearchParams();
    if (options.tagAction) params.append("tagAction", options.tagAction);
    if (options.moveTagsToRid != null) params.append("moveTagsToRid", options.moveTagsToRid);

    const query = params.toString() ? `?${params.toString()}` : "";
    return this.request(`/warehouses/${warehouseId}${query}`, {
      method: "DELETE",
    });
  }

  async getWarehouseTags(warehouseId, page = 1, limit = 100) {
    return this.request(`/warehouses/${warehouseId}/tags?page=${page}&limit=${limit}`);
  }

  async assignTagToWarehouse(warehouseId, tagId) {
    return this.request(`/warehouses/${warehouseId}/tags`, {
      method: "POST",
      body: JSON.stringify({ tagId }),
    });
  }

  async assignTagByTid(warehouseId, tid) {
    return this.request(`/warehouses/${warehouseId}/tags/by-tid`, {
      method: "POST",
      body: JSON.stringify({ tid }),
    });
  }

  async bulkAssignTags(warehouseId, tagIds) {
    return this.request(`/warehouses/${warehouseId}/tags/bulk`, {
      method: "POST",
      body: JSON.stringify({ tagIds }),
    });
  }

  async removeTagFromWarehouse(warehouseId, tagId) {
    return this.request(`/warehouses/${warehouseId}/tags/${tagId}`, {
      method: "DELETE",
    });
  }

  // Warehouse rooms endpoint
  async getWarehouseRooms(warehouseId, page = 1, limit = 100) {
    return this.request(`/warehouses/${warehouseId}/rooms?page=${page}&limit=${limit}`);
  }

  // Tags endpoints
  async getTags(page = 1, limit = 10000) {
    return this.request(`/tags?page=${page}&limit=${limit}`);
  }

  async createTag(tagData) {
    return this.request("/tags", {
      method: "POST",
      body: JSON.stringify(tagData),
    });
  }

  async updateTag(tid, tagData) {
    return this.request(`/tags/${tid}`, {
      method: "PUT",
      body: JSON.stringify(tagData),
    });
  }

  async bulkUpdateTags(updatePayload) {
    // updatePayload should have { tags: [{ tid, rid, ... }, ...] }
    return this.request(`/tags/bulk`, {
      method: "PUT",
      body: JSON.stringify(updatePayload),
    });
  }

  async deleteTag(tid) {
    // tid can be a single value or comma-separated for bulk delete
    return this.request(`/tags/${tid}`, {
      method: "DELETE",
    });
  }

  // Tags near their end-of-life — feeds the sidebar notification bell.
  async getExpiringTags(withinDays = 30) {
    return this.request(`/tags/expiring?withinDays=${withinDays}`);
  }

  // Full details for a single tag — product, hierarchy, recent audit events.
  // Powers the TagDetails page that every click-on-a-tag surface links to.
  async getTagDetails(tid) {
    return this.request(`/tags/${encodeURIComponent(tid)}/details`);
  }

  // Every physical tag for a given product EPC — used by the Inventory
  // page's "Manage expiry" modal so the admin can edit each instance.
  async getTagsByProductEpc(epc) {
    return this.request(`/tags/by-product/${encodeURIComponent(epc)}`);
  }

  // Convenience wrapper around updateTag for end-date changes. Pass null to
  // clear the field.
  async setTagEndDate(tid, endDate) {
    return this.updateTag(tid, { endDate: endDate || null });
  }

  async deleteAllTags() {
    return this.request("/tags/all", {
      method: "DELETE",
    });
  }

  async compareTags(tags, roomId = null, warehouseId = null) {
    const body = { tags };
    if (roomId !== null && roomId !== undefined) body.roomId = parseInt(roomId);
    if (warehouseId) body.warehouseId = warehouseId;
    return this.request("/tags/compare", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async cleanupTags() {
    return this.request("/cleanup", {
      method: "POST",
    });
  }

  // Rooms endpoints
  async getRooms(options = {}) {
    const params = new URLSearchParams();
    if (options.page != null) params.append("page", options.page);
    if (options.limit != null) params.append("limit", options.limit);
    if (options.forTagAssignment) params.append("forTagAssignment", "true");
    const query = params.toString();
    return this.request(`/rooms${query ? `?${query}` : ""}`);
  }

  async getRoomsByWarehouse(warehouseId, page = 1, limit = 100) {
    return this.request(`/rooms/warehouse/${warehouseId}?page=${page}&limit=${limit}`);
  }

  async getRoomById(roomId, page = 1, limit = 100) {
    return this.request(`/rooms/${roomId}?page=${page}&limit=${limit}`);
  }

  async createRoom(roomData) {
    return this.request("/rooms", {
      method: "POST",
      body: JSON.stringify(roomData),
    });
  }

  async updateRoom(roomId, roomData) {
    return this.request(`/rooms/${roomId}`, {
      method: "PUT",
      body: JSON.stringify(roomData),
    });
  }

  async deleteRoom(roomId, options = {}) {
    const params = new URLSearchParams();
    if (options.deleteTags) params.append("deleteTags", "true");
    if (options.moveTagsToRid != null) params.append("moveTagsToRid", options.moveTagsToRid);
    const query = params.toString() ? `?${params.toString()}` : "";

    return this.request(`/rooms/${roomId}${query}`, {
      method: "DELETE",
    });
  }

  // Room tag management endpoints
  async assignTagToRoom(roomId, tid) {
    return this.request(`/rooms/${roomId}/tags`, {
      method: "POST",
      body: JSON.stringify({ tid }),
    });
  }

  async bulkAssignTagsToRoom(roomId, tids) {
    return this.request(`/rooms/${roomId}/tags/bulk`, {
      method: "POST",
      body: JSON.stringify({ tids }),
    });
  }

  async bulkRemoveTagsFromRoom(roomId, tids) {
    return this.request(`/rooms/${roomId}/tags/bulk`, {
      method: "DELETE",
      body: JSON.stringify({ tids }),
    });
  }

  async removeTagFromRoom(roomId, tid) {
    return this.request(`/rooms/${roomId}/tags/${tid}`, {
      method: "DELETE",
    });
  }

  // Seed dummy data (admin only)
  async seedDummyData() {
    return this.request("/other/seed", {
      method: "POST",
    });
  }

  // Delete all data (admin only)
  async wipeAllData() {
    return this.request("/other/wipe", {
      method: "DELETE",
    });
  }

  // Transfers (manager/admin)
  async getPendingTransfers() {
    return this.request("/transfers/pending");
  }

  async acceptTransfer(tid) {
    return this.request(`/transfers/${tid}/accept`, {
      method: "POST",
    });
  }

  async rejectTransfer(tid) {
    return this.request(`/transfers/${tid}/reject`, {
      method: "POST",
    });
  }

  // Completed / historical transfers — the backend reconstructs each record
  // from the audit trail (REQUESTED + ACCEPTED/REJECTED [+ CHECKIN for
  // cross-warehouse gate completion]). Supports outcome filter, free-text
  // search, date range, and paging.
  async getTransferHistory(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") return;
      qs.set(k, String(v));
    });
    const q = qs.toString();
    return this.request(`/transfers/history${q ? `?${q}` : ""}`);
  }

}

export const api = new ApiService();
export default api;
